import { getPlaceCondensersStepId } from '@alpinehomeair/common';
import store from 'app/store';
import {
  AccuracyDataType,
  ActiveItemType,
  LevelType,
  SectionType,
  SidebarSectionType,
  SpaceType,
  StepTipType,
  SystemOptionType
} from 'data/types';
import { createElement, ReactElement } from 'react';
import { renderToString } from 'react-dom/server';
import toast from 'react-hot-toast';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { everyZoneWithItemsHasAirHandler } from 'utils/air-handler-step-complete';
import { getSignificantAppliances } from 'utils/appliances';
import { hasNoSystemsForConfiguration } from 'utils/configuration-system-availability';
import { getCoverageShortfalls } from 'utils/coverage-shortfalls';
import { reportError } from 'utils/error-log/error-log-reporter';
import { hasPlaceableExistingEquipment } from 'utils/existing-equipment';
import { hasFootprintPolygons } from 'utils/footprint-polygons';
import { isEmpty } from 'utils/helpers';
import { isBasicSizingFlowState } from 'utils/is-basic-sizing-flow';
import { resolveLevelLayoutAttachmentId } from 'utils/level-layout-image';
import {
  isHybridOnlySystemPreference,
  isPtacPreference,
  pathwayHasOutdoorCondensers
} from 'utils/project';
import { generatePtacConfigurationSections } from 'utils/ptac-configuration-sections';
import { addSpacesRequirementsMet } from 'utils/structure-add-spaces';
import { getSpaceDetailsSubSteps } from 'utils/structure-space-details';
import { sweetalertButtonClasses } from 'utils/sweetalert-button-classes';
import { normalizeTargets } from 'utils/tips';
import { buildZoneStepSections } from 'utils/zone-step-sections';
// TODO: Break circular dependency (utils/ui <-> utils/data)
import { getItem } from './data';
// TODO: Break circular dependency (utils/ui <-> utils/structure)
import {
  CONDENSER_BEFORE_ZONES_REASON,
  getAirHandlersMissingReason,
  getCondensersMissingReason,
  getLineSetsMissingReason
} from './configuration-step-reasons';
import {
  getAssignedImageForLevel,
  isLevelConditioned,
  levelHasRequiredLevelDetailsAttributes
} from './structure';
import {
  computeAccuracyFromSections,
  STEP_ACCURACY_WEIGHTS
} from './structure-accuracy';
import {
  getStructureStepMissingReason,
  structureBasicsRequirementsMet
} from './structure-step-reasons';

const sweetalert = withReactContent(Swal);

export function notification(
  message: string,
  type: 'success' | 'warning' | 'error',
  options: { duration?: number; id?: string } = {}
) {
  if (type === 'success') {
    toast.success(message, {
      ...options,
      icon: '✅'
    });
  }
  if (type === 'warning') {
    toast.error(message, {
      ...options,
      icon: '⚠️'
    });
  }
  if (type === 'error') {
    toast.error(message, {
      ...options,
      icon: '❌'
    });
  }
}

export function confirmationModal(
  message: string | ReactElement,
  confirmAction: () => void,
  title: string = 'Are you sure?',
  cancelButtonText: string = 'Cancel',
  confirmButtonText: string = 'Yes',
  swapButtonColors: boolean = false
) {
  if (Swal.isVisible()) {
    return;
  }

  sweetalert
    .fire({
      title,
      html:
        typeof message === 'string'
          ? `<span>${message}</span>`
          : renderToString(message),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      buttonsStyling: false,
      customClass: sweetalertButtonClasses(swapButtonColors)
    })
    .then((result) => {
      if (result.isConfirmed) {
        confirmAction();
      }
    });
}

type ConflictModalProps = {
  title?: string;
  message?: string | ReactElement;
  confirmButtonText?: string;
  cancelButtonText?: string;
  swapButtonColors?: boolean;
  confirmAction: () => void;
  cancelAction: () => void;
};

export function conflictModal({
  title = 'Are you sure?',
  message,
  confirmAction,
  cancelAction,
  confirmButtonText = 'Yes',
  cancelButtonText = 'Cancel',
  swapButtonColors = false
}: ConflictModalProps) {
  sweetalert
    .fire({
      title,
      html:
        typeof message === 'string'
          ? `<span>${message}</span>`
          : renderToString(message),
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText,
      cancelButtonText,
      buttonsStyling: false,
      customClass: sweetalertButtonClasses(swapButtonColors)
    })
    .then((result) => {
      if (result.isConfirmed) {
        confirmAction();
      } else {
        cancelAction();
      }
    });
}

export function warningModal(message: string, options?: SweetAlertOptions) {
  sweetalert.fire({
    ...options,
    title: 'Warning',
    text: message,
    icon: 'warning',
    showCancelButton: false,
    confirmButtonText: 'OK',
    buttonsStyling: false,
    customClass: {
      confirmButton:
        'inline-flex items-center justify-center rounded-md px-3 text-sm font-medium leading-4 shadow-sm border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 border-transparent bg-blue-600 text-white hover:bg-blue-700 py-3 m-2'
    }
  });
}

export function errorModal(
  message: string,
  options?: SweetAlertOptions,
  cause?: unknown
) {
  reportError({ source: 'errorModal', message, cause });
  sweetalert.fire({
    ...options,
    title: 'Error',
    text: message,
    icon: 'error',
    showCancelButton: false,
    confirmButtonText: 'OK',
    buttonsStyling: false,
    customClass: {
      confirmButton:
        'inline-flex items-center justify-center rounded-md px-3 text-sm font-medium leading-4 shadow-sm border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 border-transparent bg-blue-600 text-white hover:bg-blue-700 py-3 m-2'
    }
  });
}

export function successModal(
  messageOrElement: string | ReactElement,
  options?: SweetAlertOptions
) {
  sweetalert.fire({
    ...options,
    title: 'Success',
    html:
      typeof messageOrElement === 'string'
        ? `<span>${messageOrElement}</span>`
        : renderToString(messageOrElement),
    icon: 'success',
    showCancelButton: false,
    confirmButtonText: 'OK',
    buttonsStyling: false,
    customClass: {
      confirmButton:
        'inline-flex items-center justify-center rounded-md px-3 text-sm font-medium leading-4 shadow-sm border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 border-transparent bg-blue-600 text-white hover:bg-blue-700 py-3 m-2'
    }
  });
}

type CreateActiveItemParams =
  | {
      type: 'level';
      item: LevelType;
    }
  | {
      type: 'section';
      item: SectionType;
    }
  | {
      type: 'space';
      item: SpaceType;
    };

export function createActiveItem(
  params: CreateActiveItemParams
): ActiveItemType | null {
  if (params.type === 'level') {
    const item = getItem(params.item.type);

    if (!item) {
      return null;
    }

    return {
      itemId: item.id,
      uniqueId: params.item.uniqueId,
      label: params.item.label,
      attributes: params.item.attributes,
      modified: params.item.modified
    };
  }

  if (params.type === 'section') {
    return {
      uniqueId: params.item.uniqueId,
      label: params.item.label
    };
  }

  if (params.type === 'space') {
    return {
      itemId: params.item.itemId,
      uniqueId: params.item.uniqueId,
      label: params.item.label,
      attributes: params.item.attributes,
      sizingAttributes: params.item.sizingAttributes,
      modified: params.item.modified,
      length: params.item.length,
      width: params.item.width,
      additionalBTUs: params.item.additionalBTUs
    };
  }

  return null;
}

export const getComparisonValue = (
  type: 'aesthetics' | 'difficulty' | 'visibility',
  option: SystemOptionType,
  systemOptions: SystemOptionType[]
): string | number => {
  if (type === 'aesthetics') {
    const aestheticsRanks = [...systemOptions].map(
      (o) => o.averageAestheticsRank
    );
    const minRank = Math.min(...aestheticsRanks);
    const maxRank = Math.max(...aestheticsRanks);
    if (option.averageAestheticsRank === minRank) {
      return 'Least Visible';
    }
    if (option.averageAestheticsRank === maxRank) {
      return 'Most Visible';
    }
    return 'Less Visible';
  }
  if (type === 'difficulty') {
    const difficultyScores = [...systemOptions].map(
      (o) => o.installationDifficultyScore
    );
    const minScore = Math.min(...difficultyScores);
    const maxScore = Math.max(...difficultyScores);
    if (option.installationDifficultyScore === minScore) {
      return 'Easiest Installation';
    }
    if (option.installationDifficultyScore === maxScore) {
      return 'Hardest Installation';
    }
    return 'Average Installation';
  }
  if (type === 'visibility') {
    return option.outdoorVisibility ?? 'N/A';
  }
  return 'N/A';
};

export function getCoverageTooltipData(
  type: 'heating' | 'cooling',
  score: number,
  condenserCoverage: SystemOptionType['condenserCoverage']
) {
  if (score >= 100 || !condenserCoverage) {
    return null;
  }

  return getCoverageShortfalls(type, score, condenserCoverage).map(
    (shortfall) =>
      createElement(
        'div',
        {
          key: shortfall.key,
          className: 'text-center text-gray-500 font-normal'
        },
        shortfall.text
      )
  );
}

const STEP_CODES_AFTER_BASE_LAYOUT = new Set([
  'space_details',
  'extra_details',
  'sizing_tuning'
]);

export const generateStructureAccuracyData = (): AccuracyDataType => {
  const {
    structure,
    structureTypeId,
    structureAttachments,
    structureImageAssignments,
    existingSystems = []
  } = store.getState().structure;
  const { projectSettings, totalBTUs } = store.getState().project;
  const systemPreference = projectSettings.find(
    (ps) => ps.code === 'system_preference'
  )?.value;
  const isBasicSizing = isBasicSizingFlowState({
    systemPreference,
    sizingPreference: projectSettings.find(
      (ps) => ps.code === 'sizing_preference'
    )?.value
  });
  const isHybridPathway = isHybridOnlySystemPreference(systemPreference);
  const { textKeys, modifiedDefaultCategories, items, applianceItems } =
    store.getState().data;
  const { manuallyCompletedSteps, activeLevelIndex, activeSubStepIndex } =
    store.getState().ui;
  const hasFineTuningModifications = Object.values(
    modifiedDefaultCategories
  ).some(Boolean);

  const getStepText = (
    stepId: string,
    field:
      | 'title'
      | 'description'
      | 'hybrid_title'
      | 'hybrid_description'
      | `tip_${number}_title`
      | `tip_${number}_description`,
    fallback: string
  ) => {
    return textKeys[`step_${stepId}_${field}`] ?? fallback;
  };

  const noLevelsPresent = structure.length === 0;

  const levelHasLayoutImage =
    structureAttachments.length > 0 &&
    resolveLevelLayoutAttachmentId({
      structure,
      activeLevelIndex,
      structureImageAssignments
    }) !== null;

  const activeLevel = structure[activeLevelIndex];
  const activeLevelAssignedImage = activeLevel
    ? getAssignedImageForLevel(
        activeLevel.uniqueId,
        structureImageAssignments,
        structureAttachments
      )
    : null;
  const suggestedSpaceCodes = activeLevelAssignedImage
    ? activeLevel?.suggestedSpaces ?? []
    : [];
  const firstSuggestedSpaceItem = [...new Set(suggestedSpaceCodes)]
    .map((code) =>
      items.find((item) => item.group === 'Space' && item.code === code)
    )
    .find((item) => item != null);
  const addSpacesTipSpaceItem =
    firstSuggestedSpaceItem ??
    items.find((item) => item.group === 'Space' && item.code === 'bedroom');
  const addSpacesTipTargets = [
    ...(addSpacesTipSpaceItem ? [`space-${addSpacesTipSpaceItem.id}`] : []),
    'footprint-outline'
  ];

  const spaceDetailsSubSteps = getSpaceDetailsSubSteps(structure);
  const currentSpaceDetailsSubStep =
    spaceDetailsSubSteps[
      Math.min(
        Math.max(activeSubStepIndex, 0),
        Math.max(spaceDetailsSubSteps.length - 1, 0)
      )
    ];

  const getSpaceDetailsTips = (): StepTipType[] | undefined => {
    const category = currentSpaceDetailsSubStep?.category;
    const firstSpace = currentSpaceDetailsSubStep?.level.sections.flatMap(
      (section) => section.spaces
    )[0];
    const firstSpaceTargets = firstSpace
      ? [`grid-space-${firstSpace.uniqueId}`]
      : [];

    if (category === 'windows') {
      return [
        {
          targets: ['window-medium', ...firstSpaceTargets],
          title: getStepText(
            'space_details',
            'tip_1_title',
            'Add windows and doors'
          ),
          description: getStepText(
            'space_details',
            'tip_1_description',
            'Drag windows and doors from this list onto any exterior edge of a space \u2014 they snap to the outside walls of your home.'
          )
        },
        {
          targets: ['window-generator'],
          title: getStepText(
            'space_details',
            'tip_2_title',
            'Or bulk apply windows'
          ),
          description: getStepText(
            'space_details',
            'tip_2_description',
            'Set a typical window size and click Auto-Generate Windows to add windows across your whole structure at once. You can still add, move, or delete individual windows afterward.'
          )
        }
      ];
    }

    if (category === 'appliances') {
      const firstApplianceItem = getSignificantAppliances(applianceItems)[0];
      const firstApplianceTargets = firstApplianceItem
        ? [`appliance-${firstApplianceItem.itemId}`]
        : [];
      return [
        {
          targets: [...firstApplianceTargets, ...firstSpaceTargets],
          title: getStepText(
            'space_details',
            'tip_3_title',
            'Add heat-producing appliances'
          ),
          description: getStepText(
            'space_details',
            'tip_3_description',
            'Drag an appliance from this list onto the space where it lives \u2014 the heat appliances give off affects how much cooling each room needs.'
          )
        },
        {
          targets: firstSpaceTargets,
          title: getStepText(
            'space_details',
            'tip_4_title',
            'Review what we pre-filled'
          ),
          description: getStepText(
            'space_details',
            'tip_4_description',
            "We've already added typical appliances to each room. Hover over a space to see its appliances and delete any that don't belong."
          )
        }
      ];
    }

    return undefined;
  };
  const spaceDetailsTips = getSpaceDetailsTips();

  type StepConfig = {
    id: string;
    step_code: string;
    title: string;
    description: string;
    progressBarLabel: string;
    highlightTarget?: string | string[];
    tips?: StepTipType[];
    showTipsWhenComplete?: boolean;
    enableDoneButton?: boolean;
    required?: boolean;
    fullWidthContent?: boolean;
    centeredCardContent?: boolean;
    plainContent?: boolean;
    hideSidebarPanel?: boolean;
    fixedAccuracyBoost?: number;
    requiresLevels?: boolean;
    autoComplete?: boolean;
  };

  const hasConditionedLevels = structure.some((l) => isLevelConditioned(l));
  const showEquipmentPlacement = hasPlaceableExistingEquipment(
    existingSystems,
    structure
  );

  const stepConfigs: StepConfig[] = [
    {
      id: 'confirm_details',
      step_code: 'structure_details',
      progressBarLabel: 'Basic Info',
      title: getStepText('confirm_details', 'title', 'Confirm Basic Details'),
      description: getStepText(
        'confirm_details',
        'description',
        "We've estimated square footage, bedrooms, and more. Review and adjust anything that looks off."
      ),
      tips: [
        {
          targets: ['structure-details-basics'],
          title: getStepText(
            'confirm_details',
            'tip_1_title',
            'Double-check the basics'
          ),
          description: getStepText(
            'confirm_details',
            'tip_1_description',
            'We prefilled the square footage, levels, and foundation from property data. Fix anything that looks off \u2014 sizing accuracy starts here.'
          )
        }
      ],
      showTipsWhenComplete: true,
      enableDoneButton: true,
      centeredCardContent: true,
      fixedAccuracyBoost: STEP_ACCURACY_WEIGHTS.structure_details
    },
    {
      id: 'configure_level_details',
      step_code: 'floors_and_levels',
      progressBarLabel: 'Level Details',
      title: getStepText(
        'configure_level_details',
        'title',
        'Describe Each Floor'
      ),
      description: getStepText(
        'configure_level_details',
        'description',
        'Go through each floor one at a time and tell us whether it needs heating or cooling and what the ceilings are like.'
      ),
      tips: [
        {
          targets: ['level-details-active-item'],
          title: getStepText(
            'configure_level_details',
            'tip_1_title',
            'Describe each floor'
          ),
          description: getStepText(
            'configure_level_details',
            'tip_1_description',
            'Open each floor and answer the questions about heating and cooling, ceiling height, and ceiling type. A check mark appears once a floor has everything we need.'
          )
        }
      ],
      enableDoneButton: true,
      fullWidthContent: true,
      plainContent: true,
      fixedAccuracyBoost: STEP_ACCURACY_WEIGHTS.floors_and_levels,
      requiresLevels: true
    },
    {
      id: 'images',
      step_code: 'floors_and_levels',
      progressBarLabel: 'Layout Images',
      title: getStepText('images', 'title', 'Upload & Assign Layout Images'),
      description: getStepText(
        'images',
        'description',
        'Upload images of your layout, then assign them to each floor. This is optional but helpful.'
      ),
      showTipsWhenComplete: true,
      tips: [
        {
          targets: ['layout-images-upload'],
          title: getStepText('images', 'tip_1_title', 'Add your layout images'),
          description: getStepText(
            'images',
            'tip_1_description',
            "Upload floor plans, sketches, or photos from your computer, or send them straight from your phone. We'll also pull in a satellite view when available."
          )
        },
        {
          targets: ['layout-images-levels', 'layout-images-upload'],
          title: getStepText(
            'images',
            'tip_2_title',
            'Assign images to your floors'
          ),
          description: getStepText(
            'images',
            'tip_2_description',
            "Drag an image from the left onto a floor to assign it \u2014 you'll be able to crop it to just that floor's layout when you drop it."
          )
        }
      ],
      enableDoneButton: true,
      autoComplete: true,
      hideSidebarPanel: true,
      requiresLevels: true
    },
    {
      id: 'footprint',
      step_code: 'base_layout',
      progressBarLabel: 'Footprint',
      title: getStepText('footprint', 'title', "Build Your Home's Shape"),
      description: getStepText(
        'footprint',
        'description',
        'Start from a preset shape or draw the outline with a stroke — we will straighten it. Draw over an outline to extend it, or draw apart from it to add another footprint (up to 3 per floor).'
      ),
      tips: [
        {
          targets: ['footprint-shapes'],
          title: getStepText('footprint', 'tip_1_title', 'Start with a preset'),
          description: getStepText(
            'footprint',
            'tip_1_description',
            'Click a preset to place a starting outline. Choosing another preset adds a new footprint beside the existing ones (up to 3 per floor).'
          )
        },
        {
          targets: ['footprint-mode-draw'],
          title: getStepText('footprint', 'tip_2_title', 'Draw the outline'),
          description: getStepText(
            'footprint',
            'tip_2_description',
            'Draw is the default mode. Trace the outline in one stroke — we straighten the line. Draw over an outline to extend it, or draw apart from it to add a separate footprint.'
          ),
          image: '/images/structure/footprint.gif'
        },
        {
          targets: ['footprint-mode-edit'],
          title: getStepText('footprint', 'tip_3_title', 'Edit mode'),
          description: getStepText(
            'footprint',
            'tip_3_description',
            'Switch to the square pencil to drag corners and edges, move each footprint, or drag one onto the sidebar to delete it.'
          )
        },
        {
          targets: ['footprint-mode-scale'],
          title: getStepText('footprint', 'tip_4_title', 'Scale mode'),
          description: getStepText(
            'footprint',
            'tip_4_description',
            'The arrows resize every footprint on the floor proportionally \u2014 handy when the shape is right but the size is off.'
          )
        },
        ...(levelHasLayoutImage
          ? [
              {
                targets: ['footprint-mode-image'],
                title: getStepText(
                  'footprint',
                  'tip_5_title',
                  'Adjust image mode'
                ),
                description: getStepText(
                  'footprint',
                  'tip_5_description',
                  'The photo button lets you move, scale, and rotate the layout image under the grid so it lines up with your footprint.'
                )
              }
            ]
          : [])
      ],
      enableDoneButton: true,
      requiresLevels: true,
      autoComplete: false
    },
    {
      id: 'add_spaces',
      step_code: 'base_layout',
      progressBarLabel: 'Add Spaces',
      title: getStepText(
        'add_spaces',
        isHybridPathway ? 'hybrid_title' : 'title',
        isHybridPathway ? 'Add Mini-Split Spaces' : 'Add Spaces to Each Floor'
      ),
      description: getStepText(
        'add_spaces',
        isHybridPathway ? 'hybrid_description' : 'description',
        isHybridPathway
          ? 'Only add rooms without ductwork that need a mini-split air handler. Resize and place them to match your floor plan.'
          : 'Drag rooms onto the layout or use the pencil tool to draw a room, then resize and move them to match your floor plan.'
      ),
      tips: [
        {
          targets: addSpacesTipTargets,
          title: getStepText(
            'add_spaces',
            'tip_1_title',
            isHybridPathway
              ? 'Carve rooms from the leftover'
              : 'Build your floor plan'
          ),
          description: getStepText(
            'add_spaces',
            'tip_1_description',
            isHybridPathway
              ? 'The prefilled space is the leftover that becomes the ducted zone. Drag rooms onto it to carve out ductless heads.'
              : 'Drag spaces like this one onto your footprint to build your structure, then move and resize them to match your home.'
          )
        },
        {
          targets: ['space-draw-mode-draw'],
          title: getStepText('add_spaces', 'tip_2_title', 'Or draw a space'),
          description: getStepText(
            'add_spaces',
            'tip_2_description',
            isHybridPathway
              ? 'Switch to the pencil (or press D) and trace a room over the leftover in one stroke \u2014 we straighten the shape and ask what kind of room it is.'
              : 'Switch to the pencil (or press D) and trace a room inside your footprint in one stroke \u2014 we straighten the shape and ask what kind of room it is.'
          )
        }
      ],
      enableDoneButton: true,
      fixedAccuracyBoost: STEP_ACCURACY_WEIGHTS.base_layout,
      requiresLevels: true
    },
    {
      id: 'orientation',
      step_code: 'base_layout',
      progressBarLabel: 'Orientation',
      title: getStepText('orientation', 'title', 'Set Sun Direction'),
      description: getStepText(
        'orientation',
        'description',
        'This helps us calculate heat gain and size your system correctly.'
      ),
      tips: [
        {
          targets: [
            'sun-direction-top',
            'sun-direction-right',
            'sun-direction-bottom',
            'sun-direction-left'
          ],
          title: getStepText(
            'orientation',
            'tip_1_title',
            'Where does the afternoon sun hit?'
          ),
          description: getStepText(
            'orientation',
            'tip_1_description',
            'Click the side of your home that gets the afternoon sun. Afternoon sun comes from the west, so this tells us which way your home faces and how much heat it takes on.'
          )
        },
        {
          targets: ['compass'],
          title: getStepText(
            'orientation',
            'tip_2_title',
            'Fine-tune with the compass'
          ),
          description: getStepText(
            'orientation',
            'tip_2_description',
            'The N arrow shows which way north is on your floor plan. Left-click the compass to rotate the orientation clockwise, or right-click to rotate it counter-clockwise.'
          )
        }
      ],
      enableDoneButton: true,
      requiresLevels: true
    },
    ...(!pathwayHasOutdoorCondensers(systemPreference)
      ? []
      : [
          {
            id: 'condenser_locations',
            step_code: 'base_layout',
            progressBarLabel: 'Condenser Locations',
            title: 'Choose where your condensers can go',
            description:
              'Click every side of your home where a condenser can be placed. Include the roof if applicable. If you leave this blank, condensers may be placed anywhere.',
            tips: [
              {
                targets: [
                  'condenser-location-top',
                  'condenser-location-right',
                  'condenser-location-bottom',
                  'condenser-location-left',
                  'condenser-location-roof'
                ],
                title: getStepText(
                  'condenser_locations',
                  'tip_1_title',
                  'Mark where condensers can go'
                ),
                description: getStepText(
                  'condenser_locations',
                  'tip_1_description',
                  'Click every side of your home where a condenser could be installed, including the roof if that is an option. Click again to unselect. Leaving them all blank means a condenser can go anywhere.'
                )
              }
            ],
            enableDoneButton: true,
            required: false,
            requiresLevels: true
          } satisfies StepConfig
        ]),
    {
      id: 'space_details',
      step_code: 'space_details',
      progressBarLabel: 'Space Details',
      title: getStepText('space_details', 'title', 'Add Space Details'),
      description: getStepText(
        'space_details',
        'description',
        'Go through each floor and add ductwork, windows and doors, and heat sources.'
      ),
      tips: spaceDetailsTips,
      enableDoneButton: true,
      required: false,
      fixedAccuracyBoost: STEP_ACCURACY_WEIGHTS.space_details,
      requiresLevels: true
    },
    ...(showEquipmentPlacement
      ? [
          {
            id: 'equipment_placement',
            step_code: 'extra_details',
            progressBarLabel: 'Equipment Placement',
            title: getStepText(
              'equipment_placement',
              'title',
              'Place Existing Equipment (Optional)'
            ),
            description: getStepText(
              'equipment_placement',
              'description',
              'Drag your existing equipment and electrical panel onto the layout to show where they are located.'
            ),
            tips: [
              {
                targets: ['equipment-placement-list'],
                title: getStepText(
                  'equipment_placement',
                  'tip_1_title',
                  'Place your equipment'
                ),
                description: getStepText(
                  'equipment_placement',
                  'tip_1_description',
                  'Drag an item from this list onto the floor you are viewing. Drag a placed icon back to the list to remove it, or move it around the grid to reposition it. This step is optional.'
                )
              }
            ],
            enableDoneButton: true,
            required: false,
            requiresLevels: true
          } satisfies StepConfig
        ]
      : []),
    {
      id: 'sizing_tuning',
      step_code: 'sizing_tuning',
      progressBarLabel: 'Fine Tuning',
      title: getStepText(
        'sizing_tuning',
        'title',
        'Fine-Tune Details (Optional)'
      ),
      description: getStepText(
        'sizing_tuning',
        'description',
        'Adjust advanced settings like wall materials, windows, or ceiling types.'
      ),
      tips: [
        {
          targets: ['structure-details-insulation'],
          title: getStepText(
            'sizing_tuning',
            'tip_1_title',
            'Check the insulation levels'
          ),
          description: getStepText(
            'sizing_tuning',
            'tip_1_description',
            "We estimated your insulation from the home's age and climate zone. If a value looks off, drag its slider to set the level from 1 (poor) to 5 (excellent)."
          )
        },
        {
          targets: [
            'fenestration-default-settings',
            'walls-default-settings',
            'ceilings-default-settings',
            'floors-default-settings'
          ],
          title: getStepText(
            'sizing_tuning',
            'tip_2_title',
            'Fine-tune the default materials'
          ),
          description: getStepText(
            'sizing_tuning',
            'tip_2_description',
            'Open a category to adjust the default windows, walls, ceilings, and floors we assumed for your home.'
          )
        }
      ],
      enableDoneButton: true,
      required: false,
      fullWidthContent: true,
      fixedAccuracyBoost: STEP_ACCURACY_WEIGHTS.sizing_tuning,
      requiresLevels: true
    }
  ];

  const visibleStepConfigs = isBasicSizing
    ? stepConfigs.filter((config) => config.id === 'confirm_details')
    : stepConfigs;

  let previousComplete = true;
  const sections: SidebarSectionType[] = visibleStepConfigs.map((config) => {
    let isComplete: boolean;
    if (config.id === 'configure_level_details') {
      isComplete =
        structure.length > 0 &&
        hasConditionedLevels &&
        structure.every((level) =>
          levelHasRequiredLevelDetailsAttributes(level)
        );
    } else if (config.id === 'confirm_details') {
      isComplete = structureBasicsRequirementsMet(
        projectSettings,
        structureTypeId,
        structure
      );
      if (isComplete && isBasicSizing) {
        const insulation = projectSettings.find(
          (ps) => ps.code === 'insulation_level'
        )?.value;
        isComplete = !isEmpty(insulation) && Number(totalBTUs?.cooling) > 0;
      }
    } else if (config.id === 'footprint') {
      isComplete =
        structure.length > 0 &&
        structure.every((level) => hasFootprintPolygons(level));
    } else if (config.id === 'add_spaces') {
      isComplete = addSpacesRequirementsMet(structure);
    } else if (config.id === 'condenser_locations') {
      isComplete = true;
    } else if (config.id === 'orientation') {
      isComplete = manuallyCompletedSteps.includes('orientation');
    } else if (STEP_CODES_AFTER_BASE_LAYOUT.has(config.step_code)) {
      isComplete = manuallyCompletedSteps.includes(config.id);
    } else {
      isComplete =
        config.autoComplete || manuallyCompletedSteps.includes(config.id);
    }
    const isEnabled = previousComplete;

    const disabledProps =
      config.requiresLevels && noLevelsPresent
        ? { disabled: true, disabledMessage: 'Structure levels required' }
        : {};

    const requirementsMissingReason = isComplete
      ? undefined
      : getStructureStepMissingReason(config.id, {
          structure,
          projectSettings,
          structureTypeId,
          isBasicSizing,
          coolingBtu: totalBTUs?.cooling
        });

    const section: SidebarSectionType = {
      id: config.id,
      step_code: config.step_code,
      title: config.title,
      description: config.description,
      progressBarLabel: config.progressBarLabel,
      requirementsMet: isComplete,
      requirementsMissingReason,
      fullWidthContent: config.fullWidthContent,
      centeredCardContent: config.centeredCardContent,
      plainContent: config.plainContent,
      hideSidebarPanel: config.hideSidebarPanel,
      fixedAccuracyBoost: config.fixedAccuracyBoost,
      required: config.required,
      nextButtonAlwaysEnabled:
        config.id === 'sizing_tuning' || config.id === 'equipment_placement'
          ? true
          : undefined,
      steps: [
        {
          id: config.id,
          title: config.title,
          description: config.description,
          status: isComplete ? 'complete' : 'incomplete',
          tips:
            config.tips ??
            (config.highlightTarget
              ? [
                  {
                    targets: normalizeTargets(config.highlightTarget),
                    title: config.title,
                    description: config.description
                  }
                ]
              : undefined),
          showTipsWhenComplete: config.showTipsWhenComplete,
          enableDoneButton: config.enableDoneButton,
          required: config.required
        }
      ],
      ...disabledProps,
      ...(isEnabled ? {} : { disabled: true }),
      ...(config.id === 'sizing_tuning'
        ? { accuracyContributionEarned: hasFineTuningModifications }
        : {})
    };

    const neverBlocks =
      config.id === 'condenser_locations' ||
      config.id === 'orientation' ||
      STEP_CODES_AFTER_BASE_LAYOUT.has(config.step_code);
    if (!neverBlocks) {
      previousComplete = isComplete;
    }
    return section;
  });

  const overallAccuracy = Math.min(computeAccuracyFromSections(sections), 100);

  return {
    overallAccuracy,
    sections
  };
};

export const generateConfigurationSections = (
  activeConfigIndex?: number
): SidebarSectionType[] => {
  const { configurations, activeConfig: storeActiveConfig } =
    store.getState().configurations;
  const { structure } = store.getState().structure;
  const { textKeys, condenserProducts } = store.getState().data;
  const configIndex = activeConfigIndex ?? storeActiveConfig;
  const currentConfig = configurations[configIndex];

  const getStepText = (
    stepId: string,
    field:
      | 'title'
      | 'description'
      | `tip_${number}_title`
      | `tip_${number}_description`,
    fallback: string
  ) => {
    return textKeys[`step_${stepId}_${field}`] ?? fallback;
  };

  if (isPtacPreference()) {
    return generatePtacConfigurationSections(
      currentConfig,
      structure,
      getStepText
    );
  }

  const isCustomConfig = currentConfig ? !currentConfig.aiGenerated : false;

  const allConditionedSpaces = structure.flatMap((level) =>
    level.sections.flatMap((section) =>
      section.spaces.filter((space) => space.conditioned !== false)
    )
  );

  const allZonedSpaceIds = new Set(
    currentConfig?.condensers
      .flatMap((condenser) => condenser.zones)
      .flatMap((zone) => zone.zoneItems.map((item) => item.structureItemId)) ??
      []
  );

  const hasUnzonedSpaces = allConditionedSpaces.some(
    (space) => !allZonedSpaceIds.has(space.uniqueId)
  );

  const firstUnzonedSpace = allConditionedSpaces.find(
    (space) => !allZonedSpaceIds.has(space.uniqueId)
  );
  const firstUnzonedSpaceHighlightId = firstUnzonedSpace
    ? `grid-space-${firstUnzonedSpace.uniqueId}`
    : 'structure-grid';

  const allZones = currentConfig?.condensers.flatMap((c) => c.zones) ?? [];
  const currentEmptyZone = allZones.find((zone) => zone.zoneItems.length === 0);

  const allZonesWithItems =
    currentConfig?.condensers
      .flatMap((condenser) => condenser.zones)
      .filter((zone) => zone.zoneItems.length > 0) ?? [];

  const allZonesHaveAirHandlers =
    everyZoneWithItemsHasAirHandler(currentConfig);

  const allZonesWithAirHandlers = allZonesWithItems.filter(
    (zone) => zone.airHandler !== undefined
  );

  const allAirHandlersLinked =
    allZonesWithAirHandlers.length > 0 &&
    allZonesWithAirHandlers.every((zone) => {
      const airHandlerUniqueId = zone.airHandler?.uniqueId;
      return currentConfig?.lineSets?.some(
        (ls) => ls.airHandlerUniqueId === airHandlerUniqueId
      );
    });

  const configUniqueId = currentConfig?.uniqueId ?? '';
  const condensersStepKey = getPlaceCondensersStepId(configUniqueId);

  const placedCondenserCount =
    currentConfig?.condensers.filter((c) => c.position !== undefined).length ??
    0;

  const hasPlacedCondenser = placedCondenserCount > 0;

  const maxCondenserCoolingBTU =
    condenserProducts.length > 0
      ? Math.max(...condenserProducts.map((p) => p.coolingBTU))
      : 0;

  const totalZoneCoolingBTU = allZonesWithItems.reduce(
    (sum, zone) =>
      sum + zone.zoneItems.reduce((zSum, item) => zSum + item.BTUs.cooling, 0),
    0
  );

  const requiredCondensers =
    maxCondenserCoolingBTU > 0 && totalZoneCoolingBTU > 0
      ? Math.ceil(totalZoneCoolingBTU / maxCondenserCoolingBTU)
      : 1;

  const condenserRequirementsMet = placedCondenserCount >= requiredCondensers;

  // A configuration whose pool holds no system at all cannot leave this step:
  // every later step would be effort on something that cannot be built.
  const noSystemsForConfiguration = hasNoSystemsForConfiguration(currentConfig);
  const airHandlersStepComplete =
    allZonesHaveAirHandlers && !noSystemsForConfiguration;
  const lineSetsStepComplete = allAirHandlersLinked;

  const firstZoneHighlightId = allZonesWithItems[0]
    ? `zone-outline-${allZonesWithItems[0].uniqueId}`
    : 'grid-space-0';

  const zoneTips: StepTipType[] | undefined = isCustomConfig
    ? [
        {
          targets: [firstUnzonedSpaceHighlightId],
          title: getStepText('zones', 'tip_1_title', 'Group rooms into zones'),
          description: getStepText(
            'zones',
            'tip_1_description',
            'Click rooms on the floor plan to add them to the current zone. Rooms in a zone share one indoor unit.'
          )
        },
        {
          targets: ['step-accordion-zones'],
          title: getStepText('zones', 'tip_2_title', 'Manage your zones here'),
          description: getStepText(
            'zones',
            'tip_2_description',
            'This panel shows each zone, its rooms, and the condenser that serves it. Click a zone to make it active, or remove rooms you added by mistake.'
          )
        }
      ]
    : undefined;

  const zoneSections = buildZoneStepSections({
    zonesWithItems: allZonesWithItems,
    currentEmptyZone,
    hasUnzonedSpaces,
    canStartZones: hasPlacedCondenser,
    cannotStartReason: CONDENSER_BEFORE_ZONES_REASON,
    allowNewZone: hasPlacedCondenser && !allZonesHaveAirHandlers,
    tips: zoneTips
  });

  const condenserBaseTitle = getStepText(
    'condensers',
    'title',
    'Place Outdoor Unit (Condenser)'
  );
  const condenserTitle = `${condenserBaseTitle} (${placedCondenserCount}/${requiredCondensers})`;

  const airHandlerBaseTitle = getStepText(
    'air_handlers',
    'title',
    'Add Indoor Units (Air Handlers)'
  );
  const airHandlerTitle = `${airHandlerBaseTitle} (${allZonesWithAirHandlers.length}/${allZonesWithItems.length})`;

  const linkedAirHandlerCount = allZonesWithAirHandlers.filter((zone) => {
    const airHandlerUniqueId = zone.airHandler?.uniqueId;
    return currentConfig?.lineSets?.some(
      (ls) => ls.airHandlerUniqueId === airHandlerUniqueId
    );
  }).length;
  const lineSetBaseTitle = getStepText(
    'line_sets',
    'title',
    'Connect the System'
  );
  const lineSetTitle = `${lineSetBaseTitle} (${linkedAirHandlerCount}/${allZonesWithAirHandlers.length})`;

  return [
    {
      id: 'condensers',
      title: condenserTitle,
      progressBarLabel: 'Condensers',
      description: getStepText(
        'condensers',
        'description',
        'Drag and place your outdoor unit on the layout.'
      ),
      requirementsMet: condenserRequirementsMet,
      requirementsMissingReason: getCondensersMissingReason(
        placedCondenserCount,
        requiredCondensers
      ),
      required: false,
      step_code: 'condensers',
      steps: [
        {
          id: condensersStepKey,
          title: condenserTitle,
          description: getStepText(
            'condensers',
            'description',
            'Drag and place your outdoor unit on the layout.'
          ),
          status: condenserRequirementsMet ? 'complete' : 'incomplete',
          tips: isCustomConfig
            ? [
                {
                  targets: ['new-condenser', 'structure-grid'],
                  title: getStepText(
                    'condensers',
                    'tip_1_title',
                    'Place your outdoor unit'
                  ),
                  description: getStepText(
                    'condensers',
                    'tip_1_description',
                    "Drag the Condenser card onto the floor plan. Drop it along any outside wall \u2014 that's where it sits in real life."
                  )
                },
                {
                  targets: ['structure-grid'],
                  title: getStepText(
                    'condensers',
                    'tip_2_title',
                    'Add as many as you need'
                  ),
                  description: getStepText(
                    'condensers',
                    'tip_2_description',
                    'The step title shows how many condensers we estimate this home needs. You can drag a placed condenser to move it, or right-click it for more options.'
                  )
                }
              ]
            : undefined
        }
      ]
    },
    ...zoneSections,
    {
      id: 'air_handlers',
      title: airHandlerTitle,
      progressBarLabel: 'Air Handlers',
      description: getStepText(
        'air_handlers',
        'description',
        'Place an indoor unit in each zone.'
      ),
      requirementsMet: airHandlersStepComplete,
      requirementsMissingReason: getAirHandlersMissingReason(
        allZonesWithAirHandlers.length,
        allZonesWithItems.length,
        noSystemsForConfiguration
      ),
      required: false,
      step_code: 'air_handlers',
      steps: [
        {
          id: 'place_air_handlers',
          title: airHandlerTitle,
          description: getStepText(
            'air_handlers',
            'description',
            'Place an indoor unit in each zone.'
          ),
          status: airHandlersStepComplete ? 'complete' : 'incomplete',
          tips: isCustomConfig
            ? [
                {
                  targets: ['new-air-handler-high-wall', firstZoneHighlightId],
                  title: getStepText(
                    'air_handlers',
                    'tip_1_title',
                    'Place your indoor units'
                  ),
                  description: getStepText(
                    'air_handlers',
                    'tip_1_description',
                    'Drag an air handler card into each zone on the floor plan. Every zone needs one to heat and cool its rooms.'
                  )
                },
                {
                  targets: ['air-handler-list'],
                  title: getStepText(
                    'air_handlers',
                    'tip_2_title',
                    'Pick the right type for each zone'
                  ),
                  description: getStepText(
                    'air_handlers',
                    'tip_2_description',
                    'Exposed units mount on a wall or floor; concealed units hide in the ceiling for a cleaner look. Each card describes where it fits, and a warning icon means that type will probably not work for a zone.'
                  )
                }
              ]
            : undefined
        }
      ]
    },
    {
      id: 'line_sets',
      title: lineSetTitle,
      progressBarLabel: 'Line Sets',
      description: getStepText(
        'line_sets',
        'description',
        'Right-click to automatically connect and route lines.'
      ),
      requirementsMet: lineSetsStepComplete,
      requirementsMissingReason: getLineSetsMissingReason(
        linkedAirHandlerCount,
        allZonesWithAirHandlers.length
      ),
      required: false,
      step_code: 'line_sets',
      steps: [
        {
          id: 'configure_line_sets',
          title: lineSetTitle,
          description: getStepText(
            'line_sets',
            'description',
            'Right-click to automatically connect and route lines.'
          ),
          status: lineSetsStepComplete ? 'complete' : 'incomplete',
          tips: isCustomConfig
            ? [
                {
                  targets: ['structure-grid'],
                  title: getStepText(
                    'line_sets',
                    'tip_1_title',
                    'Connect the system'
                  ),
                  description: getStepText(
                    'line_sets',
                    'tip_1_description',
                    'Right-click a condenser or air handler on the floor plan to automatically connect and route the refrigerant lines.'
                  )
                },
                {
                  targets: ['auto-generate-linesets'],
                  title: getStepText(
                    'line_sets',
                    'tip_2_title',
                    'Or do them all at once'
                  ),
                  description: getStepText(
                    'line_sets',
                    'tip_2_description',
                    'Auto-generate creates and routes line sets for every condenser and air handler in this configuration. You can still adjust individual runs afterward.'
                  )
                }
              ]
            : undefined
        }
      ]
    }
  ];
};

export const getCircleClasses = (
  isComplete: boolean,
  isCurrent: boolean,
  isEnabled: boolean
) => {
  if (isCurrent) return 'border-green-600 bg-white';
  if (isComplete) return 'border-green-600 bg-green-600';
  if (isEnabled) return 'border-gray-300 bg-white hover:border-gray-400';
  return 'border-gray-300 bg-white';
};

export const getLineColorClass = (isPreviousStepComplete: boolean) =>
  isPreviousStepComplete ? 'bg-green-600' : 'bg-gray-300';

export const shouldStackLabel = (label: string) =>
  label.includes(' ') && label.split(' ').every((word) => word.length > 5);

export const getAriaLabel = (
  title: string,
  isComplete: boolean,
  isCurrent: boolean
) => {
  if (isComplete) return `${title} - Complete`;
  if (isCurrent) return `${title} - Current`;
  return `${title} - Pending`;
};

export function getModifierKey(): string {
  return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent) ? 'Cmd' : 'Ctrl';
}
